import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Alert, Col, Row, Spin, Table, notification } from "antd";

import ButtonStandard from "../../utils/button";
import TagStatus from "../../utils/tagStatus";
import { Body, Container, Footer, HeaderFields } from "./styles";
import { numberMask } from "../../../services/helpersMethodsService";
import { showFeedingAutomation } from "../../../services/feedingAutomationService";

const SITUATION_COLORS = {
  finished: {
    background: "#C5F1CA",
    borderColor: "#106518",
    color: "#106518",
  },
  open: {
    background: "#F6FFC8",
    borderColor: "#A9C133",
    color: "#A9C133",
  },
  cancelled: {
    background: "#FBC7C7",
    borderColor: "#D44C4C",
    color: "#D44C4C",
  },
};

const DrawerFeedingAutomationDetails = ({
  visible,
  manufactureId,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [manufacture, setManufacture] = useState(null);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const t = translation.feedingAutomation;

  const fetchData = useCallback(async () => {
    if (manufactureId == null) {
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await showFeedingAutomation({
        groupId,
        farmId,
        id: manufactureId,
      });
      setManufacture(data?.results ?? null);
    } catch (error) {
      console.error(error);
      notification.error({ message: t.messages.errorLoad });
    } finally {
      setIsLoading(false);
    }
  }, [groupId, farmId, manufactureId, t]);

  useEffect(() => {
    if (visible) {
      fetchData();
    } else {
      setManufacture(null);
    }
  }, [visible, fetchData]);

  const field = (label, value) => (
    <Col xs={12} sm={12} md={8} lg={8} xl={8}>
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </Col>
  );

  const flags = (record) => {
    const marks = [];
    if (record.leftover) marks.push(t.flags.leftover);
    if (record.substituted) marks.push(t.flags.substituted);
    if (record.changed) marks.push(t.flags.changed);
    if (record.realized) marks.push(t.flags.realized);

    if (marks.length === 0) {
      return <span className="empty">—</span>;
    }

    return (
      <div className="flags">
        {marks.map((mark) => (
          <span className="flag" key={mark}>
            {mark}
          </span>
        ))}
      </div>
    );
  };

  return (
    <Container
      title={
        manufacture != null ? (
          <Row type="flex" align="middle" gutter={8}>
            <Col>
              <strong>
                {t.details.title} {manufacture.treatNumber ?? ""}
              </strong>
            </Col>
            <Col>
              <TagStatus
                {...(SITUATION_COLORS[manufacture.situation] ??
                  SITUATION_COLORS.open)}
              >
                {t.situation[manufacture.situation] ?? manufacture.situation}
              </TagStatus>
            </Col>
          </Row>
        ) : (
          t.details.title
        )
      }
      width={840}
      onClose={onClose}
      maskClosable={false}
      visible={visible}
    >
      <Spin spinning={isLoading}>
        <Body>
          {manufacture != null && (
            <>
              <HeaderFields>
                <Row type="flex" gutter={8}>
                  {field(
                    t.details.manufactureDatetime,
                    manufacture.manufactureDatetime
                      ? moment(manufacture.manufactureDatetime).format(
                          "DD/MM/YYYY HH:mm",
                        )
                      : "-",
                  )}
                  {field(
                    t.details.supplementName,
                    manufacture.supplementName ?? "-",
                  )}
                  {field(t.details.wagonName, manufacture.wagonName ?? "-")}
                </Row>
                <Row type="flex" gutter={8}>
                  {field(
                    t.details.quantityPlanned,
                    `${numberMask(manufacture.quantityPlanned)} kg`,
                  )}
                  {field(
                    t.details.quantityManufactured,
                    `${numberMask(manufacture.quantityManufactured)} kg`,
                  )}
                  {field(
                    t.details.quantityManufacturedDryMatter,
                    `${numberMask(manufacture.quantityManufacturedDryMatter)} kg`,
                  )}
                </Row>
                <Row type="flex" gutter={8}>
                  {field(
                    t.details.deviationPercent,
                    manufacture.deviationPercent == null
                      ? "-"
                      : `${numberMask(manufacture.deviationPercent)}%`,
                  )}
                  {field(
                    t.details.launchType,
                    t.launchType[manufacture.launchType] ??
                      manufacture.launchType ??
                      "-",
                  )}
                  {field(
                    t.details.externalImportCode,
                    manufacture.externalImportCode ?? "-",
                  )}
                  {field(
                    t.details.stockStatus,
                    t.stockStatus?.[manufacture.stockStatus] ??
                      manufacture.stockStatus ??
                      "-",
                  )}
                </Row>
              </HeaderFields>

              {manufacture.stockStatusReason != null && (
                <Alert
                  type="warning"
                  showIcon
                  style={{
                    marginBottom: 16,
                    backgroundColor: "#FFFFFF",
                    borderColor: "#FAAD14",
                  }}
                  message={t.stockStatusHelp?.[manufacture.stockStatus] ?? ""}
                  description={(
                    t.stockReason?.[manufacture.stockStatusReason] ??
                    t.stockReason.UNEXPECTED_ERROR
                  ).replace("{{name}}", manufacture.stockStatusDetail ?? "")}
                />
              )}

              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={manufacture.ingredients ?? []}
                locale={{ emptyText: t.details.noIngredients }}
              >
                <Table.Column
                  title={t.details.columns.ingredient}
                  dataIndex="ingredientName"
                  align="left"
                />
                <Table.Column
                  title={t.details.columns.quantityPlanned}
                  dataIndex="quantityPlanned"
                  align="right"
                  render={(value) => numberMask(value)}
                />
                <Table.Column
                  title={t.details.columns.quantityRealized}
                  dataIndex="quantityRealized"
                  align="right"
                  render={(value) =>
                    value == null ? (
                      <span className="empty">—</span>
                    ) : (
                      numberMask(value)
                    )
                  }
                />
                <Table.Column
                  title={t.details.columns.deviationPercent}
                  dataIndex="deviationPercent"
                  align="right"
                  render={(value) =>
                    value == null ? (
                      <span className="empty">—</span>
                    ) : (
                      `${numberMask(value)}%`
                    )
                  }
                />
                <Table.Column
                  title={t.details.columns.flags}
                  align="left"
                  render={(text, record) => flags(record)}
                />
              </Table>

              {manufacture.observation && (
                <div className="observation">
                  {t.details.observation} {manufacture.observation}
                </div>
              )}
            </>
          )}
        </Body>
        <Footer>
          <Row type="flex">
            <Col span={24}>
              <div className="buttonsDiv">
                <ButtonStandard
                  type="button"
                  buttonType="type7"
                  onClick={onClose}
                >
                  {translation.buttons.close}
                </ButtonStandard>
              </div>
            </Col>
          </Row>
        </Footer>
      </Spin>
    </Container>
  );
};

export default DrawerFeedingAutomationDetails;
